// Long-form Danish copy for the collections that use the editorial CollectionStory
// layout. Adapted from the original farveladeland.com collection descriptions in
// lib/category-copy-original.ts, restructured into two image+text sections plus a
// rich-text block.
//
// Danish only for now — the English routes fall back to the generic seo-copy block
// in CollectionPage.
import { LocaleLink } from "@/components/LocaleLink";
import { STORY_LINK, type StoryContent } from "./CollectionStory";
import muranoWhatIs from "@/assets/farve/murano-what-is.jpg";
import muranoChooseRight from "@/assets/farve/murano-choose-right.jpg";
import bordlampeWhatIs from "@/assets/farve/bordlampe-what-is.jpg";
import bordlampeChooseRight from "@/assets/farve/bordlampe-choose-right.jpg";
import vaeglampeColoured from "@/assets/farve/coll-wall.jpg";
import vaeglampePersonality from "@/assets/farve/p4a.jpg";
import vaseCraft from "@/assets/farve/vase-craft.jpg";
import vaseColours from "@/assets/farve/vase-colours.jpg";
import glaslampeSwirl from "@/assets/farve/glaslampe-swirl.jpg";
import glaslampePeacock from "@/assets/farve/glaslampe-peacock.jpg";
import italienskLampeLyserod from "@/assets/farve/italiensk-lampe-lyserod.jpg";
import italienskLampeSwirl from "@/assets/farve/italiensk-lampe-swirl.jpg";
import vandglasLilla from "@/assets/farve/vandglas-lilla.jpg";
import vandglasBlaaSwirl from "@/assets/farve/vandglas-blaa-swirl.jpg";
import glasBordlampeSwirl from "@/assets/farve/glas-bordlampe-swirl.jpg";
import glasBordlampeRegnbue from "@/assets/farve/glas-bordlampe-regnbue.jpg";
import glasVaeglampeMessing from "@/assets/farve/glas-vaeglampe-messing.jpg";
import glasVaeglampePar from "@/assets/farve/glas-vaeglampe-par.jpg";
import glasPendelGul from "@/assets/farve/glas-pendel-gul.jpg";
import glasPendelKoekken from "@/assets/farve/glas-pendel-koekken.jpg";

const stories: Record<string, StoryContent> = {
  "murano-lamper": {
    media: [
      {
        heading: "Hvad er en Murano lampe?",
        image: muranoWhatIs,
        imageAlt: "Håndblæst Murano glaslampe med stribet mønster",
        paragraphs: [
          "En Murano lampe er en lampe fremstillet i eller inspireret af glashåndværket fra Murano, en øgruppe ved Venedig i Norditalien. Murano har i århundreder været kendt for glasproduktion, og mange vintage lamper fra området er lavet med teknikker, hvor glasset formes, farves og bearbejdes i hånden.",
          <>
            Det betyder også, at en ægte vintage Murano{" "}
            <LocaleLink to="/collections/glas-lamper" className={STORY_LINK}>
              glas lampe
            </LocaleLink>{" "}
            sjældent er helt perfekt symmetrisk. Der kan være små luftbobler, forskelle i glassets
            tykkelse eller variationer i mønsteret. For os er det ikke fejl, men en del af lampens
            karakter. Det er sådan, man kan se og mærke, at lampen ikke er masseproduceret.
          </>,
        ],
      },
      {
        heading: "Vælg den rigtige Murano lampe til dit hjem",
        image: muranoChooseRight,
        imageAlt: "Murano bordlampe med swirl-mønster foran et gardin",
        paragraphs: [
          <>
            Når du vælger en Murano lampe, handler det ikke kun om selve lampen, men også om rummet
            den skal være en del af. En stor{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              Murano loftlampe
            </LocaleLink>{" "}
            kan samle spisepladsen eller give stuen et tydeligt blikfang, mens en mindre{" "}
            <LocaleLink to="/collections/murano-bordlamper" className={STORY_LINK}>
              bordlampe
            </LocaleLink>{" "}
            eller{" "}
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              væglampe
            </LocaleLink>{" "}
            kan tilføre farve og stemning uden at fylde for meget. Leder du efter en hængelampe, kan
            du se alle vores{" "}
            <LocaleLink to="/collections/glas-pendel" className={STORY_LINK}>
              glas pendler
            </LocaleLink>{" "}
            samlet ét sted.
          </>,
          "Har du allerede farver, kunst eller vintage møbler i hjemmet, kan en lampe i Murano glas binde indretningen sammen. Bor du mere enkelt eller minimalistisk, kan én farverig lampe være nok til at give rummet personlighed. Derfor anbefaler vi, at du både kigger på størrelse, farve, lys og placering, når du vælger blandt vores Murano lamper.",
        ],
      },
    ],
    rich: [
      {
        heading: "Murano Lamper håndplukket af Farveladeland",
        paragraphs: [
          "Når du køber Murano Lamper fra Farveladeland, kan du være sikker på, at du altid får de helt klassiske italienske Murano lamper, af den fineste kvalitet. Vi håndplukker selv lamperne, og kvalitetssikrer dem inden de kommer på webshoppen. Alle vores produkter, både lamper, murano vaser, borddækning og andet interiør, er genbrugs loppefund, hvilket kendetegnes i vores fokus på at tilgodese miljøet. Læs mere om vores miljøaftryk, hvor vi kommer ind på, hvordan vi finder produkterne, samt hvordan de pakkes miljøvenligt.",
          "Pynt hjemmet med en traditionel håndlavet Murano lampe Når du overvejer at købe en lampe, kan det være mange forskellige typer af lamper, du har behov for. Heldigvis findes der Murano lamper til alle rum, og til enhver stil.",
        ],
      },
      {
        heading: "Murano Bordlamper",
        paragraphs: [
          "Den klassiske Murano bordlampe fås i mange forskellige farver, stile og størrelser. De passer perfekt til natbordet, og fungerer også super fint som et supplement til loftsbelysningen i stuen.",
          "Murano Loftlamper eller Murano Pendel, er en fantastisk lyskilde, som med sit blide og charmerende lys, kan lyse ethvert rum op. De flotte lamper kan både fås med swirl mønster, sølv og guld glimmer, samt i forskellige former og farver.",
          <>
            De klassiske{" "}
            <LocaleLink to="/collections/murano-mushroom-lampe" className={STORY_LINK}>
              Murano mushroom lamper
            </LocaleLink>{" "}
            er også blevet utroligt populære de seneste år, og det forstår vi godt. Formen er blød,
            enkel og nem at placere, mens glasset gør lampen levende, farverig og personlig. En
            mushroom lampe kan både stå som en lille stemningslampe på et natbord, som blikfang på
            en reol eller som et mere markant element i stuen.
          </>,
        ],
      },
      {
        heading: "Murano Væglamper og swirl",
        paragraphs: [
          <>
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              Murano væglamper
            </LocaleLink>{" "}
            er, ligesom de andre typer Murano lamper, kendetegnet ved deres dekorative glas,
            organiske former og flotte håndværk. De fungerer særligt godt i rum, hvor du gerne vil
            have stemningsfuld belysning uden at bruge gulv- eller bordplads, for eksempel i
            entréen, soveværelset, stuen eller en mindre lejlighed. Mange væglamper i Murano glas
            har det karakteristiske swirl mønster, som giver lampen liv og bevægelse, også når den
            ikke er tændt.
          </>,
          "Swirl er et af de mønstre, mange forbinder med klassiske Murano lamper. Mønstret passer både ind i stilrene hjem og i mere farverige eller skæve indretninger, fordi det giver lampen karakter uden nødvendigvis at dominere rummet. Derfor er en Murano lampe med swirl et godt valg, hvis du leder efter en unik vintage lampe, der både føles dekorativ og tidløs.",
        ],
      },
      {
        heading: "Køb originale vintage Murano lamper online",
        paragraphs: [
          "Det kan være svært at finde originale vintage Murano lamper i almindelige fysiske butikker. Mange af de bedste lamper findes på markeder, i dødsboer, hos private samlere eller gennem kontakter i Italien. Hos Farveladeland har vi gjort arbejdet for dig og samlet et håndplukket udvalg af Murano lamper, som du kan købe direkte online.",
          <>
            Vores Murano lamper er særligt udvalgt med blik for æstetik, kvalitet og sjældenhed. Vi
            har både klassiske{" "}
            <LocaleLink to="/collections/italienske-lamper" className={STORY_LINK}>
              italienske lamper
            </LocaleLink>{" "}
            i traditionelt Murano glas og mere originale designs, hvor du ikke finder to ens. Netop
            fordi lamperne er vintage og håndlavede, kan der være små variationer i glasset,
            asymmetri eller luftbobler. Det er ikke fejl, men en naturlig del af charmen ved
            mundblæst glas.
          </>,
          <>
            Hos Farveladeland kan du også finde retro lamper, second-hand{" "}
            <LocaleLink to="/collections/borddaekning" className={STORY_LINK}>
              borddækning
            </LocaleLink>{" "}
            og farverigt{" "}
            <LocaleLink to="/collections/interior" className={STORY_LINK}>
              interiør
            </LocaleLink>
            , som kan kombineres med dine Murano fund. Hvis du forelsker dig i flere ting, kan du
            med fordel samle til bunke. Vælg blot afhentning i butikken, og når du har produkter nok
            til en større levering, vælger du levering til din adresse. På den måde betaler du kun
            for levering én gang, vi pakker kun én gang, og fragtfirmaet skal kun forbi én gang.
          </>,
        ],
      },
    ],
  },

  "glas-lamper": {
    media: [
      {
        heading: "Når glasset gør lyset blødere",
        image: glaslampeSwirl,
        imageAlt: "Tændt hvid vintage Murano pendel med swirl-mønster på en stol med uldsæde",
        paragraphs: [
          "Glas kan noget særligt, når det bliver brugt i lamper. Det kan gøre lyset blødere, sprede det mere behageligt i rummet eller give lampen et mere levende udtryk, når farver og mønstre træder frem. Derfor er en glaslampe ikke kun en praktisk lyskilde, men også en del af stemningen i hjemmet.",
          "Nogle glaslamper er enkle og diskrete, mens andre næsten fungerer som små kunstobjekter. Hos Farveladeland går vi især efter lamper, hvor glasset har noget at byde på. Det kan være en smuk overflade, en særlig farve, et swirl mønster eller en form, der gør lampen interessant både tændt og slukket.",
        ],
      },
      {
        heading: "Glaslamper til bord, loft og væg",
        image: glaslampePeacock,
        imageAlt: "Hvid vintage Murano peacock væglampe i sollys på en træstol",
        paragraphs: [
          "En glas lampe kan bruges mange steder i hjemmet. En bordlampe i glas kan skabe hygge på et natbord, en kommode eller i et lille hjørne af stuen. En loftlampe i glas kan give rummet et mere samlet lys, mens en væglampe i glas er god, hvis du vil have dekorativ belysning uden at bruge gulv- eller bordplads.",
          <>
            Hos Farveladeland finder du løbende glaslamper i mange former og størrelser. Se blandt
            andet vores udvalg af{" "}
            <LocaleLink to="/collections/glas-bordlamper" className={STORY_LINK}>
              bordlamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              loftlamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/vaeglamper-i-glas" className={STORY_LINK}>
              væglamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/glas-pendel" className={STORY_LINK}>
              glas pendler
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/murano-mushroom-lampe" className={STORY_LINK}>
              mushroom lamper
            </LocaleLink>
            , hvis du vil gå direkte til en bestemt type lampe.
          </>,
        ],
      },
    ],
    rich: [
      {
        heading: "Farvede glaslamper med personlighed",
        paragraphs: [
          "Farvet glas kan ændre hele oplevelsen af en lampe. En grøn, blå, hvid, lyserød eller røgfarvet glaslampe kan give rummet en helt anden stemning end en klassisk lampe i metal eller stof. Nogle farver virker rolige, mens andre bliver et mere tydeligt blikfang i indretningen.",
          "Det er især her, vintage glaslamper kan noget særligt. De små variationer, farvespil og former gør, at lampen føles mere udvalgt og personlig. Hvis du går efter en lampe, der giver hjemmet karakter uden at føles masseproduceret, er en glaslampe et rigtig godt sted at starte.",
        ],
      },
      {
        heading: "En glaslampe til det særlige hjørne",
        paragraphs: [
          "Nogle lamper skal lyse hele rummet op. Andre skal bare gøre et hjørne, en kommode eller en lille plads i hjemmet lidt mere levende. En glaslampe er god til netop det, fordi den kan tilføre lys og stemning uden nødvendigvis at fylde meget.",
          <>
            Den kan stå alene som et lille blikfang eller spille sammen med andre vintagefund. Hvis
            du gerne vil skabe en mere personlig stil, kan du kombinere din glaslampe med{" "}
            <LocaleLink to="/collections/vaser" className={STORY_LINK}>
              vaser
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/glaskunst" className={STORY_LINK}>
              glaskunst
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/lysestager" className={STORY_LINK}>
              lysestager
            </LocaleLink>{" "}
            eller farverigt{" "}
            <LocaleLink to="/collections/interior" className={STORY_LINK}>
              interiør
            </LocaleLink>
            .
          </>,
        ],
      },
      {
        heading: "Murano glaslamper og italiensk håndværk",
        paragraphs: [
          "Mange af de mest særlige glaslamper kommer fra Murano-universet. Murano er kendt for mundblæst glas, farver, mønstre og håndlavede detaljer, som gør hver lampe unik. En Murano glaslampe kan derfor både fungere som belysning og som et dekorativt glasfund i hjemmet.",
          <>
            Hos Farveladeland finder du løbende lamper i Murano glas, blandt andet pendler,
            bordlamper, væglamper og mushroom lamper. Du kan se mere i vores udvalg af{" "}
            <LocaleLink to="/collections/murano-glaskunst" className={STORY_LINK}>
              Murano glas
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano lamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-bordlamper" className={STORY_LINK}>
              Murano bordlamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              Murano væglamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-mushroom-lampe" className={STORY_LINK}>
              Murano mushroom lamper
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/italienske-lamper" className={STORY_LINK}>
              italienske lamper
            </LocaleLink>
            .
          </>,
        ],
      },
      {
        heading: "Køb din næste glas lampe online",
        paragraphs: [
          "Det kan være svært at finde en glas lampe med et særligt udtryk i almindelige butikker, hvor mange lamper ligner hinanden. Hos Farveladeland samler vi løbende de vintagefund, vi selv falder for, så du kan købe glaslamper online med mere historie og personlighed.",
          "Da mange af vores lamper er vintage eller second-hand, ændrer udvalget sig hele tiden. Flere lamper findes kun i ét eller få eksemplarer, og derfor kan det være en god idé at slå til, når du finder en lampe, der passer til dit hjem. Er du i tvivl om størrelse, farve, stand eller montering, er du altid velkommen til at kontakte os via kontaktformularen eller Instagram.",
        ],
      },
    ],
  },

  "glas-pendel": {
    media: [
      {
        heading: "Når lyset får lov til at leve i glasset",
        image: glasPendelGul,
        imageAlt: "Tændt gul vintage Murano pendel med swirl-mønster under en hvid loftsroset",
        paragraphs: [
          "Noget af det særlige ved en glas pendel er måden, lyset bevæger sig gennem materialet på. Klart glas giver et let og åbent udtryk, mens farvet, rillet eller mønstret glas kan skabe mere varme og dybde i rummet. Derfor kan en pendel i glas føles både praktisk og dekorativ på samme tid.",
          "Hos Farveladeland vælger vi især glaspendler, der har noget særligt i formen eller glasset. Det kan være et blødt swirl mønster, en varm glød i glasset, en klassisk vintage form eller en lampe, der ændrer karakter, når den tændes.",
        ],
      },
      {
        heading: "Glas pendler til køkken, gang og spiseplads",
        image: glasPendelKoekken,
        imageAlt: "Hvid vintage Murano mini pendel med swirl-mønster hængende i et køkken",
        paragraphs: [
          <>
            En glas pendel kan bruges mange steder i hjemmet.{" "}
            <LocaleLink to="/blog/lamper-og-belysning/lampe-over-spisebord" className={STORY_LINK}>
              Over et spisebord
            </LocaleLink>{" "}
            kan den samle rummet og give et smukt fokuspunkt. I køkkenet kan en mindre pendel give
            lys over en bordplade eller et lille spisehjørne, mens en glaspendel i gangen kan være
            en fin velkomst, uden at lampen fylder for meget.
          </>,
          "Hvis du har et mindre rum, kan en enkelt pendel være nok til at skabe stemning. Har du mere plads, kan flere små pendler hænge sammen som et sæt. Det fungerer især godt, hvis lamperne har samme farvetone eller glasstruktur.",
        ],
      },
    ],
    rich: [
      {
        heading: "Håndplukkede glas pendler med vintagefornemmelse",
        paragraphs: [
          "Når vi udvælger glas pendler, kigger vi efter lamper, der føles særlige. Det kan være på grund af glasset, ophænget, formen eller den måde lampen hænger i rummet på. Mange af vores pendler er vintage eller second-hand fund, og derfor findes de ofte kun i ét eller få eksemplarer.",
          "Små brugsspor og variationer kan være en del af charmen ved vintage lamper. Vi beskriver altid tydelige skår, slag eller andre særlige forhold på produktsiden, så du ved, hvad du køber. Er du i tvivl om stand, ophæng eller størrelse, er du altid velkommen til at kontakte os.",
        ],
      },
      {
        heading: "Murano glas, swirl og italiensk håndværk",
        paragraphs: [
          "Mange af de mest karakterfulde glaspendler kommer fra det italienske glasunivers. Murano er især kendt for mundblæst glas, farver og mønstre, hvor hver lampe får sit eget udtryk. En pendel i Murano glas kan derfor både fungere som lyskilde og som et lille stykke glaskunst i loftet.",
          <>
            Hos Farveladeland finder du løbende pendler i forskellige størrelser, farver og former.
            Nogle er rolige og hvide, andre har swirl, varme nuancer eller mere markante mønstre i
            glasset. Hvis du vil se flere lamper i samme univers, kan du også kigge på{" "}
            <LocaleLink to="/murano" className={STORY_LINK}>
              Murano glas
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              loftlamper
            </LocaleLink>{" "}
            og vores{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano lamper
            </LocaleLink>
            .
          </>,
        ],
      },
      {
        heading: "En pendel, der passer ind uden at forsvinde",
        paragraphs: [
          "En glas pendel kan være diskret, men den behøver ikke være anonym. Den kan hænge let og roligt i rummet, men stadig give indretningen noget særligt. Det gør den god til hjem, hvor lampen gerne må føles udvalgt, uden at den overtager hele rummet.",
          <>
            Du kan kombinere en glaspendel med andre lyskilder, så rummet får flere lag af lys. Se
            for eksempel vores udvalg af{" "}
            <LocaleLink to="/collections/glas-bordlamper" className={STORY_LINK}>
              bordlamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/vaeglamper-i-glas" className={STORY_LINK}>
              væglamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-lysekrone" className={STORY_LINK}>
              lysekroner
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/murano-mushroom-lampe" className={STORY_LINK}>
              mushroom lamper
            </LocaleLink>
            , hvis du vil bygge videre på stemningen i hjemmet.
          </>,
        ],
      },
      {
        heading: "Køb glas pendel online",
        paragraphs: [
          "Det kan være svært at finde en glas pendel med personlighed i almindelige butikker, hvor mange lamper ligner hinanden. Hos Farveladeland samler vi løbende de vintagefund, vi selv falder for, så du kan købe en glas pendel online med mere historie og karakter.",
          "Udvalget ændrer sig hele tiden. Derfor kan der være perioder med små pendler, store pendler, hvide glaslamper, farvede glaspendler eller mere sjældne Murano fund. Hvis du forelsker dig i en lampe, er det værd at huske, at den sjældent findes i mange eksemplarer.",
        ],
      },
    ],
  },

  "vaeglamper-i-glas": {
    media: [
      {
        heading: "Hvad kendetegner en væglampe i glas?",
        image: glasVaeglampeMessing,
        imageAlt: "Vintage Murano væglampe med hvid glaskuppel på et beslag i messing",
        paragraphs: [
          "En væglampe i glas består typisk af et beslag, der sidder fast på væggen, og en skærm eller kuppel i glas, der former lyset. Mælkehvidt opalglas giver et jævnt og dæmpet lys, mundblæst Murano glas har mønstre og farver, der lever, når lampen er tændt, og presset glas har ofte et relief, der kaster små skygger på væggen.",
          "Beslaget er tit i messing, og en væglampe i messing og glas er en af de klassiske kombinationer. Messingen får patina med årene og bliver varmere i tonen, så den passer godt til træ og tekstiler i rummet.",
        ],
      },
      {
        heading: "Vælg din væglampe efter rummet",
        image: glasVaeglampePar,
        imageAlt: "Et par ens vintage Murano væglamper i hvidt swirl-glas med messingbeslag",
        paragraphs: [
          "I entréen lyser en væglampe i glas rummet op uden at tage plads fra knager og skoreoler. Ved sengen frigør den natbordet, og ved spejlet giver den et blødt lys, der er rarere at se sig selv i end en loftlampe. To ens lamper på hver side af en seng eller sofa giver ro, mens én lampe alene kan blive blikfanget på en tom væg.",
          <>
            Leder du specifikt efter mundblæst glas, så se vores{" "}
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              Murano væglamper
            </LocaleLink>
            . Vil du have flere stilarter at vælge imellem, finder du dem blandt vores{" "}
            <LocaleLink to="/collections/vintage-vaeglamper" className={STORY_LINK}>
              vintage væglamper
            </LocaleLink>
            , og beslag og pynt i samme metal finder du blandt vores{" "}
            <LocaleLink to="/farver/$slug" params={{ slug: "messing" }} className={STORY_LINK}>
              fund i messing
            </LocaleLink>
            .
          </>,
        ],
      },
    ],
    rich: [
      {
        heading: "Væglamper i glas håndplukket af Farveladeland",
        paragraphs: [
          "Farveladeland finder alle væglamper i glas i Italien, på loppemarkeder og hos private sælgere. Nogle er mundblæste skærme fra Murano, andre er italienske lamper i opalglas eller presset glas. Fælles for dem er, at glasset er helt, beslaget er i orden, og at lampen har et udtryk, du ikke finder i en almindelig lampebutik.",
          "Alt er brugt, så udvalget skifter hele tiden. Til gengæld får du en lampe, der allerede har bevist, at den holder, og der skal ikke produceres en ny for din skyld.",
        ],
      },
      {
        heading: "Retro væglampe i glas",
        paragraphs: [
          "Mange søger efter en retro væglampe i glas, og det er nemt at forstå. Runde kupler i opalglas, skærme formet som blomster eller svampe og beslag i messing eller emalje giver et udtryk, der passer lige så godt til en nyindrettet lejlighed som til et hus med gamle møbler. Retro handler her om stil og form. Hvor lampens alder er kendt, står den i produktbeskrivelsen.",
        ],
      },
      {
        heading: "To væglamper, der passer til det, du har",
        paragraphs: [
          "Har du allerede indrettet med møbler og lamper, du holder af, skal en ny væglampe falde ind i det. Se på metallet i dine andre lamper og på farverne i rummet, og vælg glas og beslag derefter. Et par ens væglamper på hver side af en seng, en sofa eller et spejl giver en symmetri, der får rummet til at virke gennemtænkt.",
          "Vintage væglamper dukker ikke altid op i par, så når vi finder to, der hører sammen, sælger vi dem samlet.",
        ],
      },
      {
        heading: "Væglampe i glas, når pladsen er trang",
        paragraphs: [
          "I en lejlighed med få kvadratmeter er væggen den plads, der er tilbage. En væglampe i glas tager hverken plads på natbordet eller gulvet og kan erstatte både bordlampe og standerlampe. Har lampen ledning og stik, kan du sætte den op uden elektriker og tage den med, når du flytter.",
          <>
            Vil du have mere lys for pengene, kan du kigge efter nedsatte lamper under{" "}
            <LocaleLink to="/collections/murano-pa-udsalg" className={STORY_LINK}>
              Murano på udsalg
            </LocaleLink>{" "}
            eller kombinere en enkel væglampe med andre små fund fra Farveladeland, så du kun
            betaler for én forsendelse.
          </>,
        ],
      },
      {
        heading: "Den manglende lampe til samlingen",
        paragraphs: [
          <>
            Samler du på lamper i glas, leder du måske efter noget helt bestemt: den anden lampe til
            et par, en skærm i en særlig farve eller en type glas, du ikke har endnu. Den slags
            dukker op uden varsel og forsvinder hurtigt igen. Stykker, der kun findes i ét
            eksemplar, finder du blandt{" "}
            <LocaleLink to="/alle" className={STORY_LINK}>
              alle vores unikke fund
            </LocaleLink>
            , og leder du efter noget bestemt, er du velkommen til at skrive til os.
          </>,
        ],
      },
      {
        heading: "Køb væglamper i glas online",
        paragraphs: [
          <>
            Farveladeland sender hver mandag, og glasset bliver pakket, så det ligger stille i
            kassen hele vejen. Vil du have hele rummet lyst op i samme stil, finder du også{" "}
            <LocaleLink to="/collections/glas-bordlamper" className={STORY_LINK}>
              bordlamper i glas
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              Murano loftlamper og pendler
            </LocaleLink>{" "}
            i de andre kollektioner.
          </>,
        ],
      },
    ],
  },

  "glas-bordlamper": {
    media: [
      {
        heading: "Hvordan er en bordlampe i glas lavet?",
        image: glasBordlampeSwirl,
        imageAlt:
          "Tændt vintage Murano bordlampe helt i hvidt glas med gul og grå swirl på en spejlsokkel",
        paragraphs: [
          "En bordlampe i glas kan være lavet på flere måder. Nogle har en fod i farvet glas, andre har en skærm i glas på en fod af metal, og nogle er helt i glas. Mange italienske lamper er lavet i overfanget glas, hvor et lag farvet glas ligger uden på et lag hvidt, så lampen har én farve slukket og en anden tændt.",
          "Glasset giver lampen tyngde, så den står stabilt, og den kan holde i generationer, hvis den bliver behandlet ordentligt. Derfor dukker der stadig så mange gamle glaslamper op på italienske loppemarkeder.",
        ],
      },
      {
        heading: "Vælg glasfarve efter lyset i rummet",
        image: glasBordlampeRegnbue,
        imageAlt: "Tændt rund Murano bordlampe i glas med blå og grøn swirl foran grønne planter",
        paragraphs: [
          "Glassets farve bestemmer, hvordan lyset falder i rummet. Mælkehvidt og klart glas giver et neutralt lys, der passer overalt. Grønt, ravgult og rosa glas farver lyset varmt og giver aftenstemning i stuen, mens blåt glas virker køligere og passer godt til et skrivebord.",
          <>
            Vil du se flere bordlamper, kan du kigge blandt vores{" "}
            <LocaleLink to="/collections/murano-bordlamper" className={STORY_LINK}>
              Murano bordlamper
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/vintage-bordlamper" className={STORY_LINK}>
              vintage bordlamper
            </LocaleLink>
            . Den bløde svampeform finder du blandt vores{" "}
            <LocaleLink to="/collections/murano-mushroom-lampe" className={STORY_LINK}>
              mushroom lamper
            </LocaleLink>
            .
          </>,
        ],
      },
    ],
    rich: [
      {
        heading: "Bordlamper i glas håndplukket af Farveladeland",
        paragraphs: [
          "Alle bordlamper i glas hos Farveladeland er købt i Italien på loppemarkeder og hos private sælgere. Italien har en lang tradition for lamper i glas, og det betyder, at udvalget spænder vidt: fra enkle lamper i mælkehvidt glas til lamper med swirl, striber og farver, du ikke ser i nye butikker.",
          "Hver lampe er valgt, fordi glasset er helt, og fordi den har noget, der gør den værd at tage med hjem. Udvalget skifter i takt med fundene, og en lampe, der er solgt, kommer sjældent igen i samme udgave.",
        ],
      },
      {
        heading: "Grøn bordlampe i glas og andre farver",
        paragraphs: [
          <>
            Grøn er en af de farver, flest leder efter i en bordlampe i glas, og den passer godt til
            planter, træ og hør. Ravgul og rosa glas giver et varmt skær om aftenen, mens mælkehvidt
            glas er det sikre valg, hvis rummet allerede har mange farver. Du kan se{" "}
            <LocaleLink to="/farver/$slug" params={{ slug: "gron" }} className={STORY_LINK}>
              alle fund i grønt
            </LocaleLink>{" "}
            hos Farveladeland, fra lamper til glas og keramik.
          </>,
        ],
      },
      {
        heading: "Store bordlamper i glas til kommoden og skænken",
        paragraphs: [
          "Har du et hjem, der er indrettet over mange år, skal en ny lampe kunne stå ved siden af det, du allerede har. En stor bordlampe i glas på en kommode eller skænk kan bære et helt hjørne, og to ens lamper i hver sin ende giver ro over møblet. Vælg glas, der tager en farve op fra et tæppe, et maleri eller træet i møblerne.",
          <>
            Store glaslamper er tunge og skrøbelige, så bor du tæt på København, kan du overveje at{" "}
            <LocaleLink to="/visit" className={STORY_LINK}>
              hente lampen hos os på Frederiksberg
            </LocaleLink>{" "}
            i stedet for at få den sendt.
          </>,
        ],
      },
      {
        heading: "En glaslampe, der også er pynt",
        paragraphs: [
          "Når du bor småt, skal tingene gerne kunne mere end én ting. En lille bordlampe i glas giver lys om aftenen og står som pynt i vindueskarmen eller på hylden resten af dagen, så den kan erstatte både en lampe og en vase. Farvet glas giver farve til rummet uden at tage plads.",
          <>
            Vil du have mest muligt for pengene, så hold øje med{" "}
            <LocaleLink to="/collections/murano-pa-udsalg" className={STORY_LINK}>
              Murano på udsalg
            </LocaleLink>
            , og saml gerne flere små fund i én bestilling, så du kun betaler fragt én gang.
          </>,
        ],
      },
      {
        heading: "Italiensk glas til samlingen",
        paragraphs: [
          "Samler du på italiensk glas, er bordlamperne et godt sted at lede efter teknikker, du ikke har endnu, for eksempel overfanget glas, swirl eller fødder i massivt farvet glas. Producenten er sjældent mærket på lampen, men er den kendt, står den i produktbeskrivelsen.",
        ],
      },
      {
        heading: "Køb bordlamper i glas online",
        paragraphs: [
          <>
            Farveladeland sender hver mandag, og hver glaslampe bliver pakket for sig. Mangler du
            lys flere steder i rummet, kan du supplere med{" "}
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              væglamper i glas
            </LocaleLink>{" "}
            i samme stil.
          </>,
        ],
      },
    ],
  },

  "italienske-lamper": {
    media: [
      {
        heading: "Når lampen gerne må have lidt mere sjæl og farve",
        image: italienskLampeLyserod,
        imageAlt: "Lyserød plettet vintage Murano lampe på en taburet i træ ved siden af en plante",
        paragraphs: [
          "Italienske lamper kan noget særligt, fordi de ofte kombinerer funktion og form på en måde, der føles mere personlig end almindelige standardlamper. En italiensk lampe kan være enkel og elegant, men den kan også være farverig, skulpturel eller tydeligt præget af sin tid.",
          "Hos Farveladeland vælger vi især lamper, der har karakter. Det kan være en lampe i farvet glas, en klassisk vintage form, en smuk overflade eller en detalje, der gør lampen interessant at se på, også når den ikke er tændt. Derfor finder du ikke et fast masseproduceret sortiment, men et skiftende udvalg af fund, der hver især kan give hjemmet noget særligt.",
        ],
      },
      {
        heading: "Håndplukkede italienske lamper til hjem med varme",
        image: italienskLampeSwirl,
        imageAlt: "To tændte hvide vintage Murano væglamper med swirl-mønster og messingbeslag",
        paragraphs: [
          "Når vi udvælger italienske lamper, kigger vi efter kvalitet, udtryk og den stemning, lampen kan tilføre et rum. Nogle lamper fungerer som små farvepunkter i indretningen, mens andre kan være mere markante og samle et helt område i stuen, entréen eller over spisebordet.",
          <>
            Det er netop det, der gør vintage lamper interessante. De behøver ikke ligne alt andet i
            hjemmet. En italiensk lampe kan stå i kontrast til en enkel indretning eller spille
            sammen med andre fund, farver og materialer. Hvis du især leder efter lamper i
            italiensk glas, kan du også se vores udvalg af{" "}
            <LocaleLink to="/collections/murano-glaskunst" className={STORY_LINK}>
              Murano glas
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano-lamper
            </LocaleLink>
            .
          </>,
        ],
      },
    ],
    rich: [
      {
        heading: "Små italienske lamper, store italienske lamper og alt det midt imellem",
        paragraphs: [
          "Italienske lamper findes i mange former, og den rigtige type afhænger ofte af, hvor lampen skal bruges. En bordlampe kan give blødt lys på et natbord, en kommode eller i en vindueskarm, mens en loftlampe kan samle rummet og skabe en mere tydelig lyskilde. Væglamper er oplagte, hvis du vil have dekorativ belysning uden at bruge bord- eller gulvplads.",
          <>
            Hos Farveladeland kan du løbende finde italienske lamper i flere udtryk. Se blandt andet
            vores udvalg af{" "}
            <LocaleLink to="/collections/murano-bordlamper" className={STORY_LINK}>
              bordlamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              loftlamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              væglamper
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/murano-lysekrone" className={STORY_LINK}>
              lysekroner
            </LocaleLink>
            , hvis du vil gå direkte til en bestemt type lampe.
          </>,
        ],
      },
      {
        heading: "Murano, mundblæst glas og italiensk håndværk",
        paragraphs: [
          "Mange forbinder italienske lamper med Murano, og det er ikke uden grund. Murano er kendt for sit glas og for lamper, hvor farver, mønstre og håndlavede detaljer spiller en stor rolle. En Murano lampe kan derfor både fungere som belysning og som et dekorativt glasobjekt i hjemmet.",
          "Hos Farveladeland finder du løbende vintage Murano lamper i forskellige størrelser, farver og former. Nogle har det klassiske swirl mønster, andre er mere enkle, plettede eller skulpturelle i udtrykket.",
        ],
      },
      {
        heading: "Køb italienske vintage lamper online",
        paragraphs: [
          "Det kan være svært at finde gode italienske vintage lamper i almindelige butikker. Mange af de fineste fund dukker op på markeder, hos private sælgere, i dødsboer eller gennem kontakter i Europa. Hos Farveladeland har vi gjort arbejdet for dig og samlet et håndplukket udvalg, som du kan købe direkte online.",
          <>
            Da lamperne ofte er vintage eller second-hand, kan udvalget ændre sig fra uge til uge.
            Det betyder også, at den lampe, du forelsker dig i, sjældent findes i mange eksemplarer.
            Er du i tvivl om størrelse, farve, ophæng eller om lampen passer til dit hjem, er du
            altid velkommen til at kigge forbi vores{" "}
            <LocaleLink to="/showroom" className={STORY_LINK}>
              showroom
            </LocaleLink>{" "}
            efter aftale.
          </>,
        ],
      },
    ],
  },

  vandglas: {
    media: [
      {
        heading: "Håndplukkede vandglas med personlighed",
        image: vandglasLilla,
        imageAlt: "Tre lilla vintage vandglas med mønstret bund i sollys på et rundt træbord",
        paragraphs: [
          "Når vi udvælger vandglas, kigger vi efter kvalitet, udtryk og brugbarhed. Nogle glas er enkle og klare, mens andre har farver eller mønstre. Fælles for dem er, at de skal kunne give noget ekstra til bordet.",
          "Fordi mange af vores glas er vintage eller second-hand fund, kan der være små forskelle mellem glassene. Det er ofte netop det, der gør dem charmerende. Et sæt behøver ikke være helt ens for at fungere smukt sammen. Ofte er det netop de små forskelle, der skaber en borddækning med varme og personlighed.",
        ],
      },
      {
        heading: "Vandglas til hverdag, middag og fine øjeblikke",
        image: vandglasBlaaSwirl,
        imageAlt: "Lyseblå italienske vintage vandglas med swirl-mønster på et magasin",
        paragraphs: [
          "Et godt vandglas skal være rart at holde om, nemt at bruge og pænt nok til at stå fremme på bordet. Hos Farveladeland vælger vi især vandglas, der kan bruges i hverdagen, men som stadig har lidt personlighed. Det kan være i formen, farven, tyngden eller de små detaljer i glasset.",
          "Vandglas behøver ikke være helt ens for at fungere godt sammen. Mange af vores kunder blander forskellige glas på bordet, så borddækningen bliver mere levende og mindre opstillet. Det passer særligt godt, hvis du i forvejen elsker vintage og loppefund med historie.",
        ],
      },
    ],
    rich: [
      {
        heading: "Farvede vandglas til et mere levende bord",
        paragraphs: [
          "Farvede vandglas er en nem måde at give borddækningen mere liv på. Et grønt, blåt, rosa eller ravfarvet glas kan ændre hele stemningen på bordet, uden at du behøver skifte alt andet ud. Det fungerer både til en enkel hverdagsmiddag og til et bord, hvor der gerne må være lidt mere farvelade.",
          <>
            Du kan bruge farvede vandglas alene eller blande dem med klare glas, tallerkener og
            andet second-hand service. Hvis du gerne vil skabe en gennemført borddækning, kan du
            også gå på opdagelse i vores udvalg af{" "}
            <LocaleLink to="/collections/vinglas" className={STORY_LINK}>
              vinglas
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/drinksglas" className={STORY_LINK}>
              drinksglas
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/glasskale" className={STORY_LINK}>
              glasskåle
            </LocaleLink>{" "}
            og{" "}
            <LocaleLink to="/collections/borddaekning" className={STORY_LINK}>
              borddækning
            </LocaleLink>
            .
          </>,
        ],
      },
      {
        heading: "Køb vandglas hos Farveladeland",
        paragraphs: [
          "Det kan være svært at finde vandglas med et særligt udtryk i almindelige butikker, hvor meget ligner hinanden. Hos Farveladeland samler vi løbende glas, som vi selv falder for, så du kan købe vandglas online med mere personlighed end de helt klassiske standardglas.",
          "Udvalget ændrer sig hele tiden, og mange glas findes kun i få eksemplarer eller små sæt. Derfor anbefaler vi, at du slår til, hvis du finder nogle vandglas, der passer til dit hjem. Er du i tvivl om størrelse, stand eller om glassene kan kombineres med noget, du allerede har, er du altid velkommen til at kontakte os.",
        ],
      },
    ],
  },

  "murano-bordlamper": {
    media: [
      {
        heading: "Murano bordlamper til hjem med personlighed",
        image: bordlampeWhatIs,
        imageAlt: "Hvid Murano bordlampe i ægform med swirl-mønster og messingfod på en trætaburet",
        paragraphs: [
          "En Murano bordlampe er en lille lyskilde med stor effekt. Den fylder ikke nødvendigvis meget i rummet, men den kan tilføre farve, stemning og personlighed på en måde, som en almindelig bordlampe sjældent gør. Derfor fungerer den godt både i mindre lejligheder, hvor hver ting gerne må være nøje udvalgt, og i større hjem, hvor lampen kan indgå som en del af en mere samlet indretning.",
          <>
            Måske vil du gerne se farven, størrelsen eller glasstrukturen i virkeligheden, før du
            beslutter dig. Så er du altid velkommen til at kontakte os via kontaktformularen eller
            Instagram. Du kan også gå på opdagelse i vores øvrige udvalg af{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano lamper
            </LocaleLink>
            , hvis du leder efter andre typer lamper til hjemmet.
          </>,
        ],
      },
      {
        heading: "Håndplukkede Murano bordlamper",
        image: bordlampeChooseRight,
        imageAlt: "Tændt lyserød Murano bordlampe med swirl-mønster på et sidebord foran et gardin",
        paragraphs: [
          <>
            Når du køber en Murano bordlampe hos Farveladeland, får du et unikt vintagefund, som vi
            selv har udvalgt og kvalitetssikret. Vi leder efter bordlamper med karakter, hvad enten
            det er en særlig farve, et flot swirl mønster, en klassisk mushroom form eller et mere
            enkelt udtryk i mundblæst glas. Er det især glasset, du falder for, kan du se alle vores{" "}
            <LocaleLink to="/collections/glas-bordlamper" className={STORY_LINK}>
              glas bordlamper
            </LocaleLink>{" "}
            samlet ét sted.
          </>,
          "Fordi lamperne er vintage og håndlavede, findes der ikke to, der er helt ens. Små variationer i glasset, asymmetri og luftbobler er en naturlig del af udtrykket og noget af det, der gør en ægte Murano bordlampe interessant.",
        ],
      },
    ],
    rich: [
      {
        heading: "En bordlampe til stue, soveværelse og små hjørner",
        paragraphs: [
          "En Murano bordlampe kan bruges i mange rum. På natbordet giver den et blødt og behageligt lys, på skrivebordet tilfører den personlighed, og i stuen kan den fungere som en ekstra lyskilde på en reol, et sidebord eller i vindueskarmen.",
          <>
            Den er især god, hvis du gerne vil have farve og vintage ind i hjemmet uden at vælge en
            stor loftlampe. Har du mindre plads, kan én særlig bordlampe gøre en stor forskel i
            rummet. Har du mere plads, kan den kombineres med andre vintagefund, for eksempel{" "}
            <LocaleLink to="/collections/murano-vaeglamper" className={STORY_LINK}>
              Murano væglamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              Murano loftlamper
            </LocaleLink>{" "}
            eller en dekorativ{" "}
            <LocaleLink to="/collections/murano-mushroom-lampe" className={STORY_LINK}>
              Murano mushroom lampe
            </LocaleLink>
            .
          </>,
        ],
      },
      {
        heading: "Køb vintage interiør og saml til bunke",
        paragraphs: [
          <>
            Hos Farveladeland kan du købe meget mere end bordlamper. Vi har også almindelige{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano lamper
            </LocaleLink>
            ,{" "}
            <LocaleLink to="/collections/murano-vaser" className={STORY_LINK}>
              Murano vaser
            </LocaleLink>
            , second-hand borddækning og farverigt interiør, som kan kombineres med dine Murano
            fund.
          </>,
          "Vi afsender hver mandag, og hvis du ligesom os elsker vintage og second-hand, kan du med fordel samle til bunke. Vælg blot afhentning i butikken, og når du har produkter nok til en større levering, vælger du levering til din adresse. På den måde betaler du kun for levering én gang, vi pakker kun én gang, og fragtfirmaet skal kun forbi med pakken én gang.",
        ],
      },
      {
        heading: "Køb Murano bordlamper online",
        paragraphs: [
          "Det kan være svært at finde originale vintage Murano bordlamper i almindelige fysiske butikker. Mange af de bedste lamper findes på markeder, i dødsboer, hos private samlere eller gennem kontakter i Italien. Hos Farveladeland har vi gjort arbejdet for dig og samlet et kurateret udvalg, som du kan købe direkte online.",
          <>
            Vores Murano bordlamper er særligt udvalgt med blik for æstetik, kvalitet og sjældenhed.
            Du finder både klassiske bordlamper i traditionelt Murano glas, farverige lamper,
            plettede lamper og bordlamper med det karakteristiske swirl mønster. Flere af dem
            fungerer både som praktisk belysning og som et lille{" "}
            <LocaleLink to="/collections/murano-glaskunst" className={STORY_LINK}>
              kunstobjekt
            </LocaleLink>{" "}
            i indretningen.
          </>,
        ],
      },
      {
        heading: "Sådan vælger du den rigtige Murano bordlampe",
        paragraphs: [
          "Når du vælger en Murano bordlampe, er det en god idé at tænke over både størrelse, farve og placering. En mindre lampe kan være perfekt på et natbord, en hylde eller i en vindueskarm, mens en større bordlampe bedre kan fungere som blikfang på en kommode, en konsol eller et sidebord i stuen. Farven betyder også meget: en lys eller transparent lampe giver et roligere udtryk, mens farvet eller plettet Murano glas kan skabe mere kontrast og personlighed i rummet.",
          "Hvis du bor småt, kan én særlig bordlampe være nok til at løfte hele indretningen. Har du mere plads, kan du kombinere bordlampen med andre typer belysning, for eksempel Murano væglamper eller Murano loftlamper, så rummet får flere lag af lys.",
        ],
      },
    ],
  },

  "murano-vaeglamper": {
    media: [
      {
        heading: "Murano væglamper i farvet glas",
        image: vaeglampeColoured,
        imageAlt: "Tændt rund Murano væglampe i mundblæst glas på en mørk væg",
        paragraphs: [
          "Murano væglamper findes i mange forskellige størrelser, former og farver. Hos Farveladeland kan du finde alt fra mindre væglamper på omkring 25-30 cm til større lamper og plafonder på 40-50 cm eller mere. Det gør det nemt at finde en lampe, der passer til både små rum, smalle gange og større vægflader.",
          "Udvalget skifter løbende, men du kan ofte finde væglamper i hvidt, lyserødt, blåt, gult eller plettet Murano glas. Mange af lamperne har også det klassiske swirl mønster, som giver glasset bevægelse og gør lampen dekorativ, selv når den ikke er tændt.",
          <>
            Du kan også se alle vores{" "}
            <LocaleLink to="/collections/vaeglamper-i-glas" className={STORY_LINK}>
              væglamper i glas
            </LocaleLink>{" "}
            samlet ét sted.
          </>,
        ],
      },
      {
        heading: "Væglamper i Murano med personlighed",
        image: vaeglampePersonality,
        imageAlt: "Tændt Murano plafond med swirl-mønster monteret over et lyserødt gardin",
        paragraphs: [
          "Væglamper i Murano glas er populære, fordi de både fungerer som belysning og som dekorative objekter. Glasset kan være roligt og enkelt, men også farverigt, plettet, rillet eller formet med swirl, så lampen får sit helt eget udtryk. Netop derfor passer de godt ind i hjem, hvor indretningen gerne må føles personlig og udvalgt.",
          <>
            En væglampe i glas kan gøre meget i et rum, selvom den ikke fylder fysisk. Den kan give
            en hvid væg mere liv, skabe kontrast til moderne møbler eller binde farver sammen med
            andre vintagefund som{" "}
            <LocaleLink to="/collections/murano-vaser" className={STORY_LINK}>
              Murano vaser
            </LocaleLink>
            , farverigt{" "}
            <LocaleLink to="/collections/interior" className={STORY_LINK}>
              interiør
            </LocaleLink>{" "}
            eller second-hand{" "}
            <LocaleLink to="/collections/borddaekning" className={STORY_LINK}>
              borddækning
            </LocaleLink>
            .
          </>,
        ],
      },
    ],
    rich: [
      {
        heading: "Håndplukkede Murano væglamper",
        paragraphs: [
          "Når du køber en Murano væglampe hos Farveladeland, får du et unikt vintagefund, som vi selv har udvalgt og kvalitetssikret. Vi leder efter væglamper med karakter, hvad enten det er en særlig farve, en flot glasstruktur, et klassisk swirl mønster eller en form, der fungerer godt i moderne hjem.",
          "Fordi lamperne er vintage og håndlavede, findes der ikke to, der er helt ens. Små variationer i glasset, asymmetri og luftbobler er en naturlig del af udtrykket og noget af det, der gør en ægte Murano væglampe interessant.",
        ],
      },
      {
        heading: "Sådan bruger du Murano væglamper i indretningen",
        paragraphs: [
          "En Murano væglampe er god, når du vil skabe stemning uden at bruge plads på et bord, en reol eller gulvet. I entréen kan den give et varmt første indtryk, i soveværelset kan den fungere som blød belysning ved siden af sengen, og på en gang kan flere væglamper hænge parvis og skabe en mere dekorativ rytme i rummet.",
          <>
            Hvis du bor småt, er en væglampe en oplagt måde at få farve og vintage ind i hjemmet
            uden at optage unødvendig plads. Har du større rum, kan en Murano væglampe bruges som
            supplement til andre lyskilder, for eksempel{" "}
            <LocaleLink to="/collections/murano-loftlamper" className={STORY_LINK}>
              Murano loftlamper
            </LocaleLink>{" "}
            eller{" "}
            <LocaleLink to="/collections/murano-bordlamper" className={STORY_LINK}>
              Murano bordlamper
            </LocaleLink>
            , så rummet får flere lag af lys.
          </>,
        ],
      },
      {
        heading: "Køb Murano væglamper online",
        paragraphs: [
          "Det kan være svært at finde originale vintage Murano væglamper i almindelige fysiske butikker. Mange af de bedste lamper findes på markeder, i dødsboer, hos private samlere eller gennem kontakter i Italien. Hos Farveladeland har vi gjort arbejdet for dig og samlet et kurateret udvalg, som du kan købe direkte online.",
          "Vores Murano væglamper er særligt udvalgt med blik for æstetik, kvalitet og sjældenhed. Du finder både klassiske væglamper i traditionelt Murano glas, farvede glaslamper, plettede lamper, plafonder og væglamper med swirl. Flere af dem fungerer både som praktisk belysning og som et lille kunstobjekt på væggen.",
        ],
      },
    ],
  },

  "murano-vaser": {
    media: [
      {
        heading: "Hvad gør en Murano vase særlig?",
        image: vaseCraft,
        imageAlt: "Hvid Murano vase med snoede tråde i lyserød, grøn og blå på et glasbord",
        paragraphs: [
          "En Murano vase bliver formet i hånden, mens glasset stadig er flydende. Glaspusteren arbejder med farvet glas i lag, trækker tråde ind i massen eller snor den, så mønsteret følger vasens form. Derfor bliver to vaser fra samme værksted aldrig helt ens.",
          "Vintage giver et par særlige kendetegn. Du vil ofte finde små luftbobler, en bund der ikke er helt plan, og et pontilmærke, hvor glasset har siddet på piben. Vi ser det som en del af charmen, og vi skriver altid i annoncen, hvis en vase har ridser eller skår.",
        ],
      },
      {
        heading: "Murano vaser i farver og former",
        image: vaseColours,
        imageAlt: "Lyseblå Murano vase med swirl-mønster og lyserøde liljer på et gult bord",
        paragraphs: [
          <>
            Start med farven. En lyserød eller blå Murano vase sætter et tydeligt punkt i rummet,
            mens klart og hvidt glas glider ind i en mere rolig indretning. Du kan gå direkte til{" "}
            <LocaleLink to="/farver" className={STORY_LINK}>
              Murano vaser sorteret efter farve
            </LocaleLink>{" "}
            og se, hvad der matcher dit hjem.
          </>,
          <>
            Tænk så over placeringen. Små vaser står flot i en gruppe på tre på en kommode, og en
            enkelt høj vase kan bære en vindueskarm alene. Skal glasset gå igen andre steder, finder
            du samme udtryk i vintage{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano lamper
            </LocaleLink>{" "}
            i mundblæst glas og i{" "}
            <LocaleLink to="/collections/murano-glaskunst" className={STORY_LINK}>
              Murano glas
            </LocaleLink>{" "}
            til borddækningen.
          </>,
        ],
      },
    ],
    rich: [
      {
        heading: "Murano vaser håndplukket af Farveladeland",
        paragraphs: [
          "Vi har samlet alle vores Murano vaser ét sted, så du kan se hele udvalget på én gang. Vaserne er fundet på loppemarkeder og i dødsboer i Italien, og de er gået gennem vores egne hænder, før de kom på shoppen. Vi køber ikke ind i partier og bestiller ikke efter katalog. Størrelserne spænder fra små vaser til enkelte høje stykker, og prisen følger sjældenheden mere end størrelsen.",
          "Håndplukningen betyder, at udvalget er lille og skifter tit. Vi tager en vase med, når farven, formen eller håndværket gør indtryk, og lader den stå, hvis glasset er kedeligt eller standen er for ringe. Alt hos os er genbrug, så du køber noget, der allerede findes, frem for at der skal produceres nyt.",
        ],
      },
      {
        heading: "Swirl, filigrana og massivt glas",
        paragraphs: [
          "Vaser fra Murano findes i en håndfuld klassiske teknikker. Swirl er den mest genkendelige, hvor farven snor sig op gennem glasset i en spiral. Filigrana trækker tynde hvide eller farvede tråde ind i massen i et fint gittermønster. Og så er der de massive vaser, hvor farvet glas er lagt i tykke lag, så vasen vejer mærkbart mere, end den ser ud til.",
          "Teknikken afgør, hvordan vasen opfører sig i et rum. En massiv vase kaster farvet lys ned på bordpladen, når solen rammer den, mens filigrana og striber står mere grafisk og roligt.",
        ],
      },
      {
        heading: "Murano vaser i lyserød, blå og klart glas",
        paragraphs: [
          "Farven er som regel det første, folk lægger mærke til. Vi finder oftest lyserøde og blå vaser, og det er også dem, der bliver spurgt mest efter. Grøn, ravgul og røgfarvet glas dukker op med jævne mellemrum, mens klart og mælkehvidt glas er den rolige løsning, hvis der allerede er meget farve i rummet.",
          <>
            Farverne behøver ikke matche. Tre vaser i lyserød, grøn og ravgul står ofte bedre sammen
            end tre ens. Du kan se{" "}
            <LocaleLink to="/farver" className={STORY_LINK}>
              hele shoppen sorteret efter farve
            </LocaleLink>
            , hvis du leder efter en bestemt nuance.
          </>,
        ],
      },
      {
        heading: "Vintage Murano glasvase frem for nyproduceret",
        paragraphs: [
          "Der bliver stadig blæst glas på Murano i dag, og der bliver lavet meget glas andre steder, som ligner. En vintage Murano glasvase fra 1950'erne til 1980'erne er blæst i hånden på et tidspunkt, hvor der lå flest værksteder på øen, og hvor teknikkerne blev brugt dagligt. Vægten, farvelagene og de små skævheder er svære at efterligne industrielt.",
          "Vi skriver altid, hvad vi ved om den enkelte vase, og gætter ikke på årstal eller producent, når mærket mangler.",
        ],
      },
      {
        heading: "Sådan køber du originalt vintage Murano online",
        paragraphs: [
          "Vi sender hver mandag, og alt bliver pakket i flere lag med luft hele vejen rundt i kassen, fordi gammelt glas ikke tåler at rulle. Køber du flere ting over tid, kan du vælge afhentning i butikken og få det hele sendt samlet, når du er klar. Så betaler du kun fragt én gang, og vi pakker kun én gang.",
          <>
            Bor du i eller nær København, kan du bestille en fremvisning i showroomet på
            Frederiksberg og se glasset i virkeligheden, før du beslutter dig. Ellers finder du
            resten af sortimentet under vintage{" "}
            <LocaleLink to="/collections/murano-lamper" className={STORY_LINK}>
              Murano lamper
            </LocaleLink>{" "}
            i mundblæst glas, Murano glas til{" "}
            <LocaleLink to="/collections/borddaekning" className={STORY_LINK}>
              borddækningen
            </LocaleLink>{" "}
            og farverigt vintage{" "}
            <LocaleLink to="/collections/interior" className={STORY_LINK}>
              interiør
            </LocaleLink>
            .
          </>,
        ],
      },
    ],
  },
};

/** Story layout content for a collection, or null if it uses the generic block. */
export function getCollectionStory(handle: string): StoryContent | null {
  return stories[handle] ?? null;
}
